import type { AdminSettings } from '../shared/admin.ts';
import type { BoothPrinter, MediaKind, SharedPhoto, ThemeChoice } from '../shared/cloud.ts';
import type { DevEnvHints, DevInherited, DevSettings } from '../shared/dev.ts';
import type { LedSettings, LedStatus } from '../shared/leds.ts';
export type { DevEnvHints, DevInherited, SharedPhoto, ThemeChoice };

export interface AdminState {
  settings: AdminSettings;
  files: { logos: string[]; videos: string[]; backgrounds: string[] };
}

export interface MirrorResult {
  themeId: string;
  files: number;
  downloaded: number;
  skipped: number;
  removed: number;
}

export interface SyncReport {
  themes: MirrorResult[];
  discovered: number;
  pruned?: string[];
  offline?: string;
}

export interface BoothPresence {
  lastSeen: string | null;
  online: boolean;
  media: { syncedAt: string; error?: string } | null;
  /** Null : la borne n'a encore jamais publié l'état de son ruban. */
  leds: LedStatus | null;
  /** Null : borne antérieure à la remontée du papier. */
  printer: BoothPrinter | null;
}

export interface DevState {
  settings: DevSettings;
  inherited: DevInherited;
  env: DevEnvHints;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, { credentials: 'same-origin', ...init });
  const payload = await response.json().catch(() => null);
  if (!response.ok) {
    throw new Error(payload?.error?.message ?? `${response.status} ${response.statusText}`);
  }
  return payload as T;
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

/**
 * Envoi de fichier avec avancement, directement dans le bucket.
 *
 * Cloud Run plafonne une requête à 32 Mo : une vidéo de veille ne passerait pas
 * par le serveur. Celui-ci délivre une URL signée, le navigateur y envoie le
 * fichier, puis prévient le serveur qui publie la nouvelle liste à la borne.
 *
 * `fetch` ne rapporte pas la progression d'un corps de requête : sur une vidéo
 * de plusieurs centaines de méga-octets, l'admin resterait figée sans rien
 * afficher. `XMLHttpRequest` est la seule API du navigateur qui l'expose.
 */
async function upload(kind: MediaKind, file: File, onProgress?: (ratio: number) => void) {
  const { url, file: name } = await request<{ url: string; file: string }>(
    `/api/admin/media/${kind}/upload-url`,
    json('POST', { name: file.name, type: file.type, size: file.size }),
  );

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    // Doit être celui pour lequel l'URL a été signée.
    xhr.setRequestHeader('Content-Type', file.type);

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(event.loaded / event.total);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Envoi refusé par le bucket (${xhr.status}).`));
    });
    // Une erreur réseau sans statut vient presque toujours d'une règle CORS
    // absente sur le bucket : voir le README.
    xhr.addEventListener('error', () =>
      reject(new Error('Envoi interrompu — réseau, ou CORS du bucket non configuré.')),
    );
    xhr.addEventListener('abort', () => reject(new Error('Envoi annulé.')));
    xhr.send(file);
  });

  const done = await request<{ file: string; size: number }>(
    `/api/admin/media/${kind}/complete`,
    json('POST', { file: name }),
  );
  onProgress?.(1);
  return done;
}

export const adminApi = {
  session: () =>
    request<{
      configured: boolean;
      authenticated: boolean;
      version?: string;
      user?: { name: string; email: string } | null;
    }>('/api/admin/session'),

  booth: () => request<BoothPresence>('/api/admin/booth'),

  themes: () => request<{ themes: ThemeChoice[] }>('/api/admin/themes').then((r) => r.themes),

  /** URL de l'aperçu ; `version` force le navigateur à refetcher après un enregistrement. */
  previewUrl: (themeId: string, version: string) =>
    `/api/admin/preview?themeId=${encodeURIComponent(themeId)}&v=${encodeURIComponent(version)}`,

  logout: () => request<{ ok: true }>('/api/admin/logout', { method: 'POST' }),

  load: () => request<AdminState>('/api/admin/settings'),

  save: (patch: unknown) => request<{ settings: AdminSettings }>('/api/admin/settings', json('PUT', patch)),

  uploadLogo: (file: File, onProgress?: (ratio: number) => void) => upload('logos', file, onProgress),
  deleteLogo: (name: string) =>
    request<{ ok: true }>(`/api/admin/media/logos/${encodeURIComponent(name)}`, { method: 'DELETE' }),

  uploadVideo: (file: File, onProgress?: (ratio: number) => void) =>
    upload('screensaver', file, onProgress),
  deleteVideo: (name: string) =>
    request<{ ok: true }>(`/api/admin/media/screensaver/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  uploadBackground: (file: File, onProgress?: (ratio: number) => void) =>
    upload('backgrounds', file, onProgress),
  deleteBackground: (name: string) =>
    request<{ ok: true }>(`/api/admin/media/backgrounds/${encodeURIComponent(name)}`, {
      method: 'DELETE',
    }),

  loadDev: () => request<DevState>('/api/admin/dev'),

  saveDev: (patch: Partial<DevSettings>) =>
    request<{ settings: DevSettings }>('/api/admin/dev', json('PUT', patch)),

  refreshTheme: () => request<{ report: SyncReport }>('/api/admin/theme/refresh', { method: 'POST' }),
  shares: () => request<{ photos: SharedPhoto[]; retentionDays: number }>('/api/admin/shares'),
  deleteShare: (token: string) =>
    request<{ ok: true }>(`/api/admin/shares/${encodeURIComponent(token)}`, { method: 'DELETE' }),

  testLeds: (leds: LedSettings) =>
    request<{ port: string }>('/api/admin/leds/test', json('POST', leds)),
};
